"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as React from "react";
import { CircleX } from "lucide-react";

import { cn } from "@/lib/utils";
import { CLOSE_BTN_CATEGORY } from "@/types/common";

/**
 * Figma Atlas 2827:14608 `vuesax/bold/close-circle` (node 2827:14593 frame).
 *
 * Exported because lead popups that draw their own close button (they need it
 * inside the card, not on the dialog wrapper) must use this exact path rather
 * than copying it — see lead drop 2001.
 */
export function LeadDropAtlasCloseIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M12 2C6.49 2 2 6.49 2 12C2 17.51 6.49 22 12 22C17.51 22 22 17.51 22 12C22 6.49 17.51 2 12 2ZM15.36 14.3C15.65 14.59 15.65 15.07 15.36 15.36C15.21 15.51 15.02 15.58 14.83 15.58C14.64 15.58 14.45 15.51 14.3 15.36L12 13.06L9.7 15.36C9.55 15.51 9.36 15.58 9.17 15.58C8.98 15.58 8.79 15.51 8.64 15.36C8.35 15.07 8.35 14.59 8.64 14.3L10.94 12L8.64 9.7C8.35 9.41 8.35 8.93 8.64 8.64C8.93 8.35 9.41 8.35 9.7 8.64L12 10.94L14.3 8.64C14.59 8.35 15.07 8.35 15.36 8.64C15.65 8.93 15.65 9.41 15.36 9.7L13.06 12L15.36 14.3Z"
        fill="currentColor"
      />
    </svg>
  );
}

// Wrapper to ensure pointer-events are properly restored on body when dialog closes
const Dialog = ({
  onOpenChange,
  open,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root>) => {
  const handleOpenChange = React.useCallback(
    (isOpen: boolean) => {
      // Ensure pointer-events are restored when dialog closes
      if (!isOpen) {
        // Use setTimeout to ensure this runs after Radix UI's cleanup
        setTimeout(() => {
          if (document.body.style.pointerEvents === "none") {
            document.body.style.pointerEvents = "";
          }
        }, 0);
      }
      onOpenChange?.(isOpen);
    },
    [onOpenChange]
  );

  // Cleanup on unmount if dialog is open
  React.useEffect(() => {
    return () => {
      // Ensure pointer-events are restored when component unmounts
      setTimeout(() => {
        if (document.body.style.pointerEvents === "none") {
          document.body.style.pointerEvents = "";
        }
      }, 0);
    };
  }, []);

  return <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange} {...props} />;
};

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-[110] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
));

DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    closeBtnType?: CLOSE_BTN_CATEGORY;
    dialogOverlayStyle?: string;
    closeBtnStyle?: string;
  }
>(
  (
    {
      className,
      children,
      closeBtnType = CLOSE_BTN_CATEGORY.DEFAULT,
      dialogOverlayStyle = "",
      closeBtnStyle = "",
      ...props
    },
    ref
  ) => (
    <DialogPortal>
      <DialogOverlay className={dialogOverlayStyle} />
      <div className="fixed inset-0 z-[110] flex items-center justify-center pointer-events-none">
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            "relative z-[110] grid w-full gap-4 border-slate-200 bg-white p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg dark:border-slate-800 pointer-events-auto",
            className
          )}
          {...props}
        >
          <DialogTitle className="hidden"></DialogTitle>
          {children}
          {closeBtnType === CLOSE_BTN_CATEGORY.LEADDROP_WITH_LEAD_TITLE && (
            <DialogPrimitive.Close
              type="button"
              className={cn(
                "absolute right-3 top-3 z-[60] flex size-8 items-center justify-center rounded-full text-white opacity-95 outline-none transition-opacity hover:opacity-100 focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent",
                closeBtnStyle,
              )}
              aria-label="Close"
            >
              <LeadDropAtlasCloseIcon className="size-6 shrink-0" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
          {closeBtnType == CLOSE_BTN_CATEGORY.DEFAULT && (
            <DialogPrimitive.Close
              className={cn(
                "absolute right-4 top-4 z-[111] rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-slate-950 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-slate-100 data-[state=open]:text-slate-500 md:hidden dark:ring-offset-slate-950 dark:focus:ring-slate-300 dark:data-[state=open]:bg-slate-800 dark:data-[state=open]:text-slate-400",
                closeBtnStyle
              )}
            >
              <CircleX className="size-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </div>
    </DialogPortal>
  )
);

DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
);

DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
);

DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
));

DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-slate-500 dark:text-slate-400", className)}
    {...props}
  />
));

DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
